import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FolderOpen, Loader2, Lock, BellRing } from 'lucide-react';
import { haccpApi, erroreHaccp } from '../haccpApi';
import { ProfiloLink } from './ProfiloLink';
import { CartelleProfilo } from './CartelleProfilo';
import { RegistroAccessi } from './RegistroAccessi';
import { AnteprimaIspettore } from './AnteprimaIspettore';

const BREVI = { asl: 'ASL e NAS', sicurezza: 'Sicurezza lavoro', vigili_fuoco: 'Vigili del fuoco', comune: 'Comune' };
const quando = (iso) => new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const Intro = ({ ultima }) => (
  <div className="rounded-2xl bg-slate-900 text-white p-5 relative overflow-hidden" data-testid="owner-inspector-intro">
    <FolderOpen className="absolute -right-5 -bottom-7 h-36 w-36 text-white/5" />
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">Senza carta</p>
    <h3 className="text-lg font-bold mt-1">Cartella pronta per l'ispettore</h3>
    <p className="text-sm text-slate-300 mt-1 max-w-lg">Fai scansionare il QR: l'ispettore vede in sola lettura documenti e registri degli ultimi 3 mesi, sempre aggiornati e divisi in cartelle. Ti avvisiamo appena lo apre.</p>
    {ultima && (
      <p className="mt-3 text-xs text-slate-200 flex items-center gap-1.5" data-testid="owner-last-access">
        <BellRing className="h-3.5 w-3.5 text-amber-300" />Ultima apertura: {quando(ultima.quando)} · {ultima.profilo_nome}
      </p>
    )}
  </div>
);

const SceltaProfilo = ({ profili, scelto, onScegli }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" data-testid="owner-profile-tabs">
    {profili.map((p) => (
      <button key={p.codice} type="button" role="tab" aria-selected={scelto === p.codice} onClick={() => onScegli(p.codice)} data-testid={`owner-profile-tab-${p.codice}`}
        className={`shrink-0 px-4 h-10 rounded-full text-sm font-semibold flex items-center gap-2 border transition-colors ${scelto === p.codice ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-500'}`}>
        <span className={`h-2 w-2 rounded-full ${p.attivo ? 'bg-emerald-500' : 'bg-slate-300'}`} />{BREVI[p.codice] || p.nome}
      </button>
    ))}
  </div>
);

export const CartellaIspettoreSection = () => {
  const [dati, setDati] = useState(null);
  const [vietato, setVietato] = useState(false);
  const [scelto, setScelto] = useState('asl');
  const [anteprima, setAnteprima] = useState(false);

  const carica = useCallback(async () => {
    try { setDati(await haccpApi.get('/ispettore')); } catch (e) {
      if (e?.response?.status === 403) setVietato(true); else toast.error(erroreHaccp(e, 'Cartella non disponibile, riprova'));
    }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const agisci = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(erroreHaccp(e, 'Operazione non riuscita')); }
    await carica();
  };

  if (vietato) {
    return (
      <div className="p-5"><div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-3" data-testid="owner-inspector-solo-responsabile">
        <Lock className="h-5 w-5 text-gray-500" /><p className="text-sm text-gray-700">La cartella per l'ispettore la gestisce solo il responsabile.</p>
      </div></div>
    );
  }
  if (!dati) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  const profilo = dati.profili.find((p) => p.codice === scelto) || dati.profili[0];
  const p = `/ispettore/${profilo.codice}`;
  return (
    <div className="p-4 lg:p-6 space-y-5" data-testid="owner-inspector-section">
      <Intro ultima={dati.accessi.find((a) => a.azione === 'apertura')} />
      <SceltaProfilo profili={dati.profili} scelto={profilo.codice} onScegli={setScelto} />
      <ProfiloLink profilo={profilo} onAnteprima={() => setAnteprima(true)}
        onAttivo={(v) => agisci(() => haccpApi.post(`${p}/attivo`, { attivo: v }), v ? 'Link attivo' : 'Link sospeso')}
        onRigenera={() => agisci(() => haccpApi.post(`${p}/rigenera`), 'Nuovo link creato: il vecchio QR non funziona più')} />
      <CartelleProfilo profilo={profilo} cartelle={dati.cartelle} onSalva={(lista) => agisci(() => haccpApi.put(`${p}/cartelle`, { cartelle: lista }))} />
      <RegistroAccessi accessi={dati.accessi} profilo={profilo.codice} />
      <AnteprimaIspettore profilo={anteprima ? profilo.codice : null} onClose={() => setAnteprima(false)} />
    </div>
  );
};

export default CartellaIspettoreSection;
